import React from 'react';
import { Alert, TableRow, TableCell } from '@mui/material';

// DT-1478: amounts with no exchange rate are never converted at 1:1 (DT-1472). The APIs leave
// them out of the converted totals and report them per native currency; these helpers show
// them so the screen never reads as if they did not exist.

// Sum a per-currency map field ({ EUR: 12.5, MAD: 300 }) and a count field across months.
export function sumUnconverted(rows, mapField, countField) {
  const byCurrency = {};
  let count = 0;
  for (const r of rows || []) {
    count += Number((r && r[countField]) || 0);
    const m = (r && r[mapField]) || {};
    for (const [ccy, amt] of Object.entries(m)) {
      byCurrency[ccy] = (byCurrency[ccy] || 0) + Number(amt || 0);
    }
  }
  return { count, byCurrency };
}

function fmtNative(amount, ccy) {
  const num = Number(amount || 0);
  if (!ccy) return `${num.toFixed(2)} (currency unknown)`;
  try {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: ccy }).format(num);
  } catch (e) {
    return `${ccy} ${num.toFixed(2)}`;
  }
}

export function formatByCurrency(byCurrency) {
  return Object.entries(byCurrency || {})
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([ccy, amt]) => fmtNative(amt, ccy))
    .join(' · ');
}

export function hasUnconverted(group) {
  return !!group && (Number(group.count || 0) > 0 || Object.keys(group.byCurrency || {}).length > 0);
}

// Banner listing every group that has unconverted amounts. groups: [{ label, count, unit, byCurrency }]
export function UnconvertedFxBanner({ groups, targetCurrency }) {
  const shown = (groups || []).filter(hasUnconverted);
  if (shown.length === 0) return null;
  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <strong>Some amounts are not converted to {targetCurrency}.</strong> There is no stored monthly
      exchange rate for their currency and month, so they are left out of the {targetCurrency} totals
      below (never counted 1:1). Load the missing rates to include them.
      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {shown.map((g) => (
          <li key={g.label}>
            {g.label}: {formatByCurrency(g.byCurrency)}
            {Number(g.count || 0) > 0 ? ` (${g.count} ${g.unit || 'rows'})` : ''}
          </li>
        ))}
      </ul>
    </Alert>
  );
}

// One table row per group, for the breakdown tables ("Not converted: EUR 10.00 · MAD 300.00").
export function UnconvertedFxRow({ group, targetCurrency, colSpan = 1 }) {
  if (!hasUnconverted(group)) return null;
  return (
    <TableRow>
      <TableCell sx={{ color: 'warning.main' }}>
        {group.label}: not converted, no {targetCurrency} rate (not in the totals)
      </TableCell>
      <TableCell align="right" colSpan={colSpan} sx={{ color: 'warning.main' }}>
        {formatByCurrency(group.byCurrency)}
      </TableCell>
    </TableRow>
  );
}
